import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import GeneralModal from "../../../../../../components/dialogs/GeneralModal";
import WorkFlow from "../../../../../../libs/api/services/WorkFlow";
import WorkFlowDocumentContainer from "../../../../workflow/management/Sections/Documents/WorkFlowDocumentContainer";
import WorkFlowNotesSection from "../../../../workflow/management/Sections/WorkFlowNotesSection";
import WorkFlowManagementContext from "../../../../workflow/management/WorkFlowManagementProvider";
import PatientEventTypeWorkFlow from "../PatientEventTypeWorkFlow";

const resolveHistoryId = (value) => {
  if (!value) return undefined;

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    const directCandidates = [
      value.id,
      value.Id,
      value.ID,
      value.eventId,
      value.EventId,
      value.workflowEventId,
      value.WorkflowEventId,
      value.workFlowEventId,
      value.WorkFlowEventId,
      value.wfEventId,
      value.WFEventId,
      value.historyId,
      value.HistoryId,
      value.workflowHistoryId,
      value.WorkflowHistoryId,
      value.wfEventHistoryId,
      value.WFEventHistoryId,
    ];

    const directMatch = directCandidates.find((candidate) => candidate != null && candidate !== "");
    if (directMatch) return directMatch;

    const nestedCandidates = [
      value.wfEvent,
      value.WFEvent,
      value.event,
      value.Event,
      value.workflowEvent,
      value.WorkflowEvent,
      value.workFlowHistory,
      value.WorkFlowHistory,
      value.historyEntry,
      value.HistoryEntry,
    ];

    for (const nestedValue of nestedCandidates) {
      const nestedMatch = resolveHistoryId(nestedValue);
      if (nestedMatch) return nestedMatch;
    }
  }

  return undefined;
};

const normalizeDateTime = (value) => {
  if (!value) return undefined;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const normalizeDateOnly = (value) => {
  if (!value) return undefined;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toIsoWithFallbackTime = (dateOnly, fallbackDateTime) => {
  const normalizedDate = normalizeDateOnly(dateOnly);
  if (!normalizedDate) return undefined;

  const fallback = normalizeDateTime(fallbackDateTime) || new Date().toISOString();
  const fallbackDate = new Date(fallback);
  if (Number.isNaN(fallbackDate.getTime())) return `${normalizedDate}T00:00:00.000Z`;

  // Keep an existing time component when users only change the date.
  const hh = String(fallbackDate.getHours()).padStart(2, "0");
  const mm = String(fallbackDate.getMinutes()).padStart(2, "0");
  const ss = String(fallbackDate.getSeconds()).padStart(2, "0");
  const combined = new Date(`${normalizedDate}T${hh}:${mm}:${ss}`);
  if (Number.isNaN(combined.getTime())) return `${normalizedDate}T00:00:00.000Z`;
  return combined.toISOString();
};

export default function WorkFlowPatientHistoryAddItemModal({
  show,
  setShowModal,
  type,
}) {
  const { id } = useParams();
  const { load } = useContext(WorkFlowManagementContext);
  const [selectedEventId, setSelectedEventId] = useState();
  const [selectedDate, setSelectedDate] = useState();
  const [createdHistoryEvent, setCreatedHistoryEvent] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [persistedSelection, setPersistedSelection] = useState({
    historyId: undefined,
    eventId: undefined,
    triggeredDateTime: undefined,
  });
  const dateInputValue =
    normalizeDateOnly(selectedDate) ||
    normalizeDateOnly(persistedSelection.triggeredDateTime) ||
    normalizeDateOnly(new Date());

  useEffect(() => {
    if (!show) {
      setSelectedEventId(undefined);
      setSelectedDate(undefined);
      setCreatedHistoryEvent(undefined);
      setIsSaving(false);
      setPersistedSelection({ historyId: undefined, eventId: undefined, triggeredDateTime: undefined });
    }
  }, [show]);

  const handleClose = () => {
    setSelectedEventId(undefined);
    setSelectedDate(undefined);
    setCreatedHistoryEvent(undefined);
    setIsSaving(false);
    setPersistedSelection({ historyId: undefined, eventId: undefined, triggeredDateTime: undefined });
    load();
    setShowModal(undefined);
  };

  useEffect(() => {
    if (!show || !selectedEventId || !id || isSaving) return;

    const persistedDateTime = normalizeDateTime(persistedSelection.triggeredDateTime);
    const selectedDateOnly = normalizeDateOnly(selectedDate);
    const persistedDateOnly = normalizeDateOnly(persistedDateTime);
    const selectedDateTime =
      toIsoWithFallbackTime(selectedDateOnly, persistedDateTime || new Date()) ||
      persistedDateTime;

    if (!createdHistoryEvent) {
      const historyDate = selectedDateTime || new Date().toISOString();
      setIsSaving(true);

      WorkFlow.CreateWorkFlowHistory(id, selectedEventId, historyDate)
        .then((historyEvent) => {
          const historyEventId = resolveHistoryId(historyEvent);
          const normalizedHistoryEvent = {
            ...historyEvent,
            id: historyEventId,
            eventId: historyEventId,
            workflowEventId: historyEventId,
            workflowHistoryId: historyEventId,
            historyId: historyEventId,
          };

          setCreatedHistoryEvent(normalizedHistoryEvent);
          setPersistedSelection({
            historyId: historyEventId,
            eventId: selectedEventId,
            triggeredDateTime: historyDate,
          });
          load();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsSaving(false);
        });
      return;
    }

    if (!persistedSelection.historyId) return;

    const eventChanged = persistedSelection.eventId !== selectedEventId;
    const dateChanged =
      !!selectedDateOnly &&
      selectedDateOnly !== persistedDateOnly &&
      !!selectedDateTime;

    if (!eventChanged && !dateChanged) return;

    setIsSaving(true);
    WorkFlow.UpdateWorkFlowHistory(
      persistedSelection.historyId,
      eventChanged ? selectedEventId : undefined,
      dateChanged ? selectedDateTime : undefined
    )
      .then((historyEvent) => {
        const historyEventId = resolveHistoryId(historyEvent) || persistedSelection.historyId;
        setCreatedHistoryEvent((previous) => {
          const prevEvent = previous || {};
          return {
            ...prevEvent,
            ...historyEvent,
            id: historyEventId,
            eventId: historyEventId,
            workflowEventId: historyEventId,
            workflowHistoryId: historyEventId,
            historyId: historyEventId,
            notes: historyEvent?.notes ?? prevEvent.notes ?? [],
            documents: historyEvent?.documents ?? prevEvent.documents ?? [],
          };
        });
        setPersistedSelection({
          historyId: historyEventId,
          eventId: selectedEventId,
          triggeredDateTime: selectedDateTime || persistedDateTime,
        });
        load();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [
    createdHistoryEvent,
    id,
    isSaving,
    load,
    persistedSelection.eventId,
    persistedSelection.historyId,
    persistedSelection.triggeredDateTime,
    selectedDate,
    selectedEventId,
    show,
  ]);

  return (
    <GeneralModal show={show} onClose={handleClose} title="Log patient event">
      <div className="p-3 d-flex flex-column gap-3 flex-grow-1">
        <div>
          <label className="fw-bold d-block mb-2">Select the type of event</label>
          <PatientEventTypeWorkFlow onChanged={setSelectedEventId} defaultValue={selectedEventId} />

        </div>

        {selectedEventId && (
          <div>
            <label className="fw-bold d-block mb-2">Event Date</label>
            <input
              type="date"
              className="form-control"
              value={dateInputValue || ""}
              onChange={(e) => setSelectedDate(e.target.value || undefined)}
            />
          </div>
        )}

        {isSaving && <div className="text-muted">Saving workflow entry...</div>}

        {createdHistoryEvent && (
          <>
            {type === "note" ? (
              <WorkFlowNotesSection event={createdHistoryEvent} notes={createdHistoryEvent.notes ?? []} className="mt-2" />
            ) : (
              <WorkFlowDocumentContainer event={createdHistoryEvent} docs={createdHistoryEvent.documents ?? []} />
            )}
          </>
        )}
      </div>
    </GeneralModal>
  );
}
